import { useEffect, useRef } from 'react';
import { useRive, Layout, Fit, Alignment, useViewModel, useViewModelInstance, useViewModelInstanceNumber, useViewModelInstanceString, useViewModelInstanceBoolean, decodeFont, useViewModelInstanceEnum, EventType } from '@rive-app/react-canvas';
import { useSound } from '../components/SoundHandler';
import { useAssets } from '../components/AssetHandler';
import { DEVICE_PIXEL_RATIO } from '../game-engine';

interface ResultModalProps {
  level: number;
  winAmount: number; // USD value
  onComplete: () => void;
}

export const ResultModal: React.FC<ResultModalProps> = ({
  level,
  winAmount,
  onComplete,
}) => {
  const { assets } = useAssets();
  const sound = useSound();
  const bungeeFontBytes = assets?.fonts?.['bungee'];

  const { rive, RiveComponent } = useRive({
    buffer: assets?.riveFiles?.['result'],
    artboard: 'result',
    stateMachines: 'state',
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
    assetLoader: (asset, bytes) => {
      if (asset.cdnUuid.length > 0 || bytes.length > 0) {
        return false;
      }

      if (!asset.isFont || !bungeeFontBytes) {
        return false;
      }

      const fontBytes = new Uint8Array(bungeeFontBytes);
      decodeFont(fontBytes).then(font => {
        asset.setFont(font);
        font.unref();
      });
      return true;
    },
    autoBind: true,
  }, { customDevicePixelRatio: DEVICE_PIXEL_RATIO });

  const viewModel = useViewModel(rive, { name: 'result' });
  const viewModelInstance = useViewModelInstance(viewModel, { useDefault: true, rive });
  
  const { setValue: setLevel } = useViewModelInstanceNumber('level', viewModelInstance);
  const { setValue: setWinAmount } = useViewModelInstanceNumber('win-amount', viewModelInstance);
  const { value: ended } = useViewModelInstanceBoolean('ended', viewModelInstance);

  const refPlayHighlightLeft = useRef(0);

  useEffect(() => {
    if (viewModelInstance) {
      const bonusLevel = level - 1
      setLevel(bonusLevel);

      refPlayHighlightLeft.current = bonusLevel
      refPlayHighlightLeft.current -= 1
      sound.play('resultHighlight');
    }
  }, [level, setLevel, viewModelInstance]);

  useEffect(() => {
    if (viewModelInstance) {
      setWinAmount(winAmount);
    }
  }, [winAmount, setWinAmount, viewModelInstance]);

  useEffect(() => {
    if (ended) {
      onComplete();
    }
  }, [ended, onComplete]);

  useEffect(() => {
    if (!rive) return;

    const onRiveEvent = (riveEvent: any) => {
      const eventName = riveEvent.data.name;
      if (eventName === 'count-start') {
        sound.play('resultCount');
      } else if (eventName === 'count-end') {
        sound.stop('resultCount', 1000);
      } else if (eventName === 'light-circle') {
        if (refPlayHighlightLeft.current > 0) {
          sound.play('resultHighlight');
          refPlayHighlightLeft.current -= 0;
        }
      }
    };

    rive.on(EventType.RiveEvent, onRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, onRiveEvent);
      sound.stop('resultCount');
      sound.stop('resultHighlight');
    };
  }, [rive]);

  return (
    <div className="fixed inset-0 z-50">
      <div className="w-full h-full relative">
         <RiveComponent className="w-full h-full" />
      </div>
    </div>
  );
};
